import { useFacilityStore } from '@/features/_stores'
import type {
  TExposedApplicationStatusHistoryDTO,
  TFindApplicationStatusHistoryInputDTO,
} from '@doutorsim/shared/application-status-history'
import type {
  TCreateApplicationInputDTO,
  TCreateApplicationResponseBodyDTO,
  TCreateApplicationWithPersonInputDTO,
  TCreateApplicationWithPersonResponseBodyDTO,
  TCreateGuarantorInputDTO,
  TCreateGuarantorResponseBodyDTO,
  TExposedApplicationAggregateDTO,
  TFindApplicationByIdResponseBodyDTO,
  TFindApplicationsByFacilityResponseBodyDTO,
  TBulkSimulateApplicationInputDTO,
  TBulkSimulateApplicationResponseBodyDTO,
  TReleaseApplicationHandoffResponseBodyDTO,
} from '@doutorsim/shared/applications'
import { apiClient } from '../api'

export interface GetApplicationsParams {
  limit?: number
  cursor?: string
  direction?: 'forward' | 'backward'
  search?: string
  name?: string
  cpf?: string
  status?: string[]
  startDate?: string
  endDate?: string
  facilityIds?: string[]
}

export function buildApplicationSearchParams(params?: GetApplicationsParams): URLSearchParams {
  const queryParams = new URLSearchParams()
  if (!params) return queryParams

  if (params.limit) queryParams.append('limit', String(params.limit))
  if (params.cursor) {
    queryParams.append('cursor', params.cursor)
    queryParams.append('direction', params.direction || 'forward')
  }
  if (params.search) queryParams.append('search', params.search)
  if (params.name) queryParams.append('name', params.name)
  if (params.cpf) queryParams.append('cpf', params.cpf)
  if (params.status) {
    params.status.forEach((s) => queryParams.append('status', s))
  }
  if (params.startDate) queryParams.append('startDate', params.startDate)
  if (params.endDate) queryParams.append('endDate', params.endDate)
  if (params.facilityIds) {
    params.facilityIds.forEach((id) => queryParams.append('facilityIds', id))
  }

  return queryParams
}

class ApplicationsApi {
  private getBaseUrl() {
    const facility = useFacilityStore.getState().currentFacility

    if (!facility?.id) throw new Error('Company or facility not selected')

    return `/api/v1/companies/${facility.companyId}/facilities/${facility.id}/applications`
  }

  async getApplications(
    companyId: string,
    facilityId: string,
    params?: GetApplicationsParams,
  ): Promise<TFindApplicationsByFacilityResponseBodyDTO> {
    const baseUrl = `/api/v1/companies/${companyId}/facilities/${facilityId}/applications`
    const queryParams = buildApplicationSearchParams(params)

    const {
      data: applications,
      meta,
      error,
    } = await apiClient.get<TExposedApplicationAggregateDTO[]>(
      `${baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
    )

    if (error || !applications) {
      console.error('Failed to fetch applications:', error?.title, error?.detail)
      return { data: [], meta: { hasMore: false } }
    }

    return {
      data: applications,
      meta: meta ?? { hasMore: false },
    }
  }

  async getApplicationById(id: string, companyId?: string, facilityId?: string) {
    const baseUrl =
      companyId && facilityId
        ? `/api/v1/companies/${companyId}/facilities/${facilityId}/applications`
        : this.getBaseUrl()
    return apiClient.get<TFindApplicationByIdResponseBodyDTO>(`${baseUrl}/${id}`)
  }

  async createApplication(data: TCreateApplicationInputDTO) {
    const baseUrl = this.getBaseUrl()
    return apiClient.post<TCreateApplicationResponseBodyDTO>(baseUrl, data)
  }

  async updateApplication(id: string, data: Partial<TCreateApplicationInputDTO>) {
    const baseUrl = this.getBaseUrl()
    return apiClient.patch<TCreateApplicationResponseBodyDTO>(`${baseUrl}/${id}`, data)
  }

  async deleteApplication(id: string) {
    const baseUrl = this.getBaseUrl()
    return apiClient.delete(`${baseUrl}/${id}`)
  }

  async bulkSimulateApplication(data: TBulkSimulateApplicationInputDTO) {
    const baseUrl = this.getBaseUrl()
    return apiClient.post<TBulkSimulateApplicationResponseBodyDTO>(`${baseUrl}/bulk-simulate`, data)
  }

  async getApplicationStatusHistory(
    dto: TFindApplicationStatusHistoryInputDTO,
  ): Promise<TExposedApplicationStatusHistoryDTO[]> {
    const { applicationId, companyId, facilityId } = dto
    const baseUrl = `/api/v1/companies/${companyId}/facilities/${facilityId}/applications`
    const { data: history, error } = await apiClient.get<TExposedApplicationStatusHistoryDTO[]>(
      `${baseUrl}/${applicationId}/status-history`,
    )

    if (error || !history) {
      console.error('Failed to fetch status history:', error?.title, error?.detail)
      return []
    }

    // Transform dates from strings to Date objects
    return history.map((item) => ({
      ...item,
      changedAt: new Date(item.changedAt),
      createdAt: new Date(item.createdAt),
    }))
  }

  async createApplicationWithPersons(data: TCreateApplicationWithPersonInputDTO) {
    const baseUrl = `/api/v1/companies/${data.companyId}/facilities/${data.facilityId}/applications`
    return apiClient.post<TCreateApplicationWithPersonResponseBodyDTO>(
      `${baseUrl}/with-persons`,
      data,
    )
  }

  async releaseHandoff(applicationId: string, companyId: string, facilityId: string) {
    const baseUrl = `/api/v1/companies/${companyId}/facilities/${facilityId}/applications`
    return apiClient.post<TReleaseApplicationHandoffResponseBodyDTO>(
      `${baseUrl}/${applicationId}/release-handoff`,
      {},
    )
  }

  async createGuarantor(applicationId: string, data: TCreateGuarantorInputDTO) {
    const baseUrl = this.getBaseUrl()
    return apiClient.put<TCreateGuarantorResponseBodyDTO>(
      `${baseUrl}/${applicationId}/guarantors`,
      data,
    )
  }
}

export const applicationsApi = new ApplicationsApi()
